import PQueue from 'p-queue'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table.js'
import { ImageArrayValue, isValidUrl } from '@makyoapp/makyo-common'

import { IDB } from '@/core/db'
import { parseStdColumn } from '@/core/models/columns/parsers/parser'

import { actionUpload } from './action-upload'
import { APP_FILE_UPLOAD_PATH, APP_IMAGE_UPLOAD_PATH } from './constants'
import { isFile } from './utils'

export function sanitizeWhatsappMessage(message: string) {
  return message
    .replace(/\n/g, '\n') // Normalize newlines
    .split('\n') // Split into lines
    .map(
      (line: string) =>
        line
          .replace(/&/g, '%26') // Encode ampersands
          .replace(/#/g, '%23') // Encode hash
          .replace(/\+/g, '%20') // Replace plus with space encoding
    )
    .join('%0A') // Join lines with encoded newline
}

export function addCacheBusterToFirstUrl(message: string, paramName = 't'): string {
  if (!message) return message

  const urlMatch = message.match(/https?:\/\/[^\s]+/i)
  if (!urlMatch) return message

  const original = urlMatch[0]
  try {
    const url = new URL(original)
    url.searchParams.set(paramName, Date.now().toString())
    const busted = url.toString()
    return message.replace(original, busted)
  } catch {
    return message
  }
}

export const extractFilename = (url: string): string => {
  const pathComponents = url.split('/')
  return pathComponents[pathComponents.length - 1]
}

export const downloadFile = (fileUrl: string) => {
  return new Promise<void>((resolve, reject) => {
    if (!isValidUrl(fileUrl)) reject()

    const fileName = extractFilename(fileUrl)

    fetch(fileUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        a.click()
        a.remove()
      })
      .then(() => {
        resolve()
      })
      .catch(() => {
        console.error('Download file failed!')
        reject()
      })
  })
}

export const playAudio = (url: string, maxDuration = 5000): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    const audio = new Audio(url)
    let timeoutId: NodeJS.Timeout

    const cleanup = () => {
      audio.pause()
      audio.currentTime = 0
      audio.removeEventListener('ended', onEnded)
      clearTimeout(timeoutId)
    }

    const onEnded = () => {
      cleanup()
      resolve()
    }

    audio.addEventListener('ended', onEnded)

    audio
      .play()
      .then(() => {
        timeoutId = setTimeout(() => {
          cleanup()
          resolve()
        }, maxDuration)
      })
      .catch((error) => {
        cleanup()
        console.warn('Audio play error:', error)
        reject(error)
      })
  })
}

export const openGoogleMaps = (address: string) => {
  return new Promise<void>((resolve, reject) => {
    try {
      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
      window.open(url, '_blank')
      resolve()
    } catch (e) {
      reject()
    }
  })
}

export const copyToClipboard = (text: string) => {
  return new Promise<void>((resolve, reject) => {
    // Modern browsers with Clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(text)
        .then(() => resolve())
        .catch((error) => reject(error))
    } else {
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()

        const successful = document.execCommand('copy')
        document.body.removeChild(textArea)

        if (successful) {
          resolve()
        } else {
          reject(new Error('Copy command was unsuccessful'))
        }
      } catch (error) {
        reject(error)
      }
    }
  })
}

const pqueue = new PQueue({ concurrency: 1 })

async function uploadColumnFile(columnID: string, file: File, currentOrgId: string, uploadFolderId: string) {
  try {
    const uploadResult = await actionUpload(file, currentOrgId, uploadFolderId)
    return uploadResult
  } catch (error) {
    console.error(`Failed to upload file for column ${columnID}:`, error)
    return null
  }
}

/**
 * Prepare form payload for the given data and table
 */
export async function prepareFormPayload(
  data: Record<string, any>,
  tableId: string,
  dbClient: IDB,
  currentOrgId: string
) {
  if (!dbClient || !data) return {}

  const table = dbClient.tablesMap.get(tableId)
  if (!table) return {}

  // Early filter for file columns to avoid multiple iterations
  const fileColumns = Object.entries(data).reduce(
    (acc, [col, value]) => {
      const column = table.columnsMap.get(col)
      if (column) {
        if (isFile(value) && (column._Type === ColumnTypes.Image || column._Type === ColumnTypes.Attachment)) {
          acc.single[col] = value as File
        } else if (Array.isArray(value) && column._Type === ColumnTypes.ImageArray) {
          // Separate files and existing URLs
          const files: File[] = []
          const existingUrls: ImageArrayValue[] = []

          value.forEach((item) => {
            if (isFile(item)) {
              files.push(item as File)
            } else if (typeof item === 'object') {
              existingUrls.push(item)
            }
          })

          if (files.length > 0) {
            acc.array[col] = {
              files,
              existingUrls,
            }
          }
        }
      }
      return acc
    },
    {
      single: {} as Record<string, File>,
      array: {} as Record<string, { files: File[]; existingUrls: ImageArrayValue[] }>,
    }
  )

  // Process single file uploads (unchanged)
  const uploadedSingleFiles =
    Object.keys(fileColumns.single).length > 0
      ? await Promise.all(
          Object.entries(fileColumns.single).map(async ([col, file]) => {
            const columnType = table.columnsMap.get(col)?._Type
            const uploadResult = await pqueue.add(() =>
              uploadColumnFile(
                col,
                file,
                currentOrgId,
                columnType === ColumnTypes.Image || columnType === ColumnTypes.ImageArray
                  ? APP_IMAGE_UPLOAD_PATH
                  : APP_FILE_UPLOAD_PATH
              )
            )
            return [col, uploadResult?._File._Link] as const
          })
        ).then(Object.fromEntries)
      : {}

  // Process array file uploads with merging
  const uploadedArrayFiles =
    Object.keys(fileColumns.array).length > 0
      ? await Promise.all(
          Object.entries(fileColumns.array).map(async ([col, { files, existingUrls }]) => {
            // batch upload files
            const uploadedUrls = await Promise.all(
              files.map(async (file) => {
                const columnType = table.columnsMap.get(col)?._Type
                const uploadResult = await pqueue.add(() =>
                  uploadColumnFile(
                    col,
                    file,
                    currentOrgId,
                    columnType === ColumnTypes.Image || columnType === ColumnTypes.ImageArray
                      ? APP_IMAGE_UPLOAD_PATH
                      : APP_FILE_UPLOAD_PATH
                  )
                )
                return uploadResult
                  ? ({
                      Url: uploadResult?._File._Link,
                      Title: '',
                      Description: '',
                      CreatedAt: uploadResult?._File._CreatedAt,
                      Owner: '',
                      Size: uploadResult?._File._Size,
                      Width: 0,
                      Height: 0,
                      Tags: [],
                      Format: '',
                    } as ImageArrayValue)
                  : null
              })
            )
            // Merge existing URLs with newly uploaded ones
            return [col, [...existingUrls, ...uploadedUrls.filter(Boolean)]] as const
          })
        ).then(Object.fromEntries)
      : {}

  // Single pass through data to build final payload
  const finalPayload = Object.entries(data).reduce(
    (acc, [col, value]) => {
      const column = table.columnsMap.get(col)
      if (!column) return acc

      let parsedCell = parseStdColumn(column._Type, column._Settings, value, table.columnsMap, undefined, undefined, {
        timezone: table.settings._Tz,
      })

      let parsedValue = parsedCell.value

      switch (true) {
        case column._Type === ColumnTypes.DateTime: {
          parsedValue = parsedCell.value ? (parsedCell.value as Date).toISOString() : ''
          break
        }
        // Handle single file columns
        case column._Type === ColumnTypes.Image || column._Type === ColumnTypes.Attachment: {
          parsedValue = isFile(value) ? uploadedSingleFiles[col] : value
          break
        }
        // Handle image array columns
        case column._Type === ColumnTypes.ImageArray: {
          if (Array.isArray(value) && value.some(isFile)) {
            parsedValue = uploadedArrayFiles[col] ?? []
          }
          break
        }
        default: {
          // Only include non-null/non-empty values
          parsedValue = parsedCell.value
        }
      }

      acc[col] = parsedValue

      return acc
    },
    {} as Record<string, any>
  )

  return finalPayload
}
